import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, CheckCircle, AlertTriangle, Search, FileText, TrendingUp, DollarSign, Calendar, CreditCard, X, Download, ChevronDown, ChevronRight, RefreshCw, Layers, Filter } from 'lucide-react';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../utils/usePermissions';
import './DashboardClientsMaster.css';

const API_URL = import.meta.env.VITE_API_URL;

const EXPORT_FIELD_GROUPS = [
    {
        id: 'identification',
        title: 'Identification Client',
        icon: Users,
        fields: [
            { key: 'refSteg', label: 'Réf. STEG / Pompage', default: true },
            { key: 'clientNom', label: 'Nom & Prénom / Raison sociale', default: true },
            { key: 'cin', label: 'CIN / N° Passeport', default: true },
            { key: 'telephone', label: 'Numéro de Téléphone', default: true }
        ]
    },
    {
        id: 'dossier',
        title: 'Informations Dossier',
        icon: FileText,
        fields: [
            { key: 'commercial', label: 'Commercial assigné', default: true },
            { key: 'typeDossier', label: 'Type de dossier (STEG, Comptant, Pompage)', default: true },
            { key: 'etatDossier', label: 'État actuel du dossier', default: true }
        ]
    },
    {
        id: 'dates',
        title: 'Chronologie & Dates Clés',
        icon: Calendar,
        fields: [
            { key: 'dateAjout', label: "Date d'ajout", default: true },
            { key: 'dateArrivDossier', label: "Date d'arrivée dossier", default: false },
            { key: 'dateDepotSteg', label: 'Date dépôt STEG', default: false },
            { key: 'dateDemandeReception', label: 'Date demande réception', default: false },
            { key: 'dateLivraison', label: 'Date réception', default: false },
            { key: 'dateMiseEnService', label: 'Date mise en service', default: false }
        ]
    },
    {
        id: 'finance',
        title: 'Finances & Situation de Paiement',
        icon: DollarSign,
        fields: [
            { key: 'montantTotal', label: 'Montant total du dossier', default: true },
            { key: 'montantPaye', label: 'Montant déjà payé', default: true },
            { key: 'montantRestant', label: 'Montant restant à payer', default: true },
            { key: 'statutPaiement', label: 'Statut global de paiement', default: true },
            { key: 'typePaiement', label: 'Structure / Type de paiement', default: false }
        ]
    },
    {
        id: 'echeances',
        title: 'Échéances & Échéancier',
        icon: CreditCard,
        fields: [
            { key: 'nbEcheancesTotal', label: "Nombre total d'échéances", default: true },
            { key: 'nbEcheancesPayees', label: 'Échéances entièrement payées', default: true },
            { key: 'nbEcheancesPartielles', label: 'Échéances partiellement payées', default: false },
            { key: 'nbEcheancesEnAttente', label: 'Échéances en attente (futures)', default: false },
            { key: 'nbEcheancesEnRetard', label: 'Échéances en retard', default: true },
            { key: 'montantTotalEcheances', label: 'Montant total des échéances', default: false },
            { key: 'montantPayeEcheances', label: 'Montant payé sur échéances', default: false },
            { key: 'montantRestantEcheances', label: 'Montant restant sur échéances', default: false },
            { key: 'prochaineEcheance', label: 'Prochaine échéance à venir', default: true },
            { key: 'detailEcheances', label: '📑 Feuille 2 : Détail des échéances (1 ligne par échéance)', default: false, isDetailSheet: true }
        ]
    }
];

const DashboardClientsMaster = ({ onNavigate, onLogout, currentPage }) => {
    const { hasPermission, typeUser } = usePermissions();
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState({
        stats: { 
            totalDossiers: 0, dossiersPayes: 0, dossiersNonPayes: 0, pourcentagePayes: 0,
            totalPaiementsCount: 0, montantTotalEncaisse: 0, clientsPayants: 0, clientsNonPayants: 0
        },
        stegNonPayes: [],
        receptionneNonCommences: [],
        paiementsEffectues: []
    });
    const [pipelineData, setPipelineData] = useState({ totalDossiers: 0, pipeline: [] });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [commercialFilter, setCommercialFilter] = useState('');
    const [tableFilters, setTableFilters] = useState({ commercial: '', methode: '', statut: '' });
    const [sortConfig, setSortConfig] = useState({ key: 'dateDesc', direction: 'desc' });

    // Period Filters
    const [period, setPeriod] = useState('total');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [customDates, setCustomDates] = useState({ start: '', end: '' });

    // --- Export Modal State ---
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportPeriod, setExportPeriod] = useState('total');
    const [exportYear, setExportYear] = useState('');
    const [exportMonth, setExportMonth] = useState('');
    const [exportCustomDates, setExportCustomDates] = useState({ start: '', end: '' });
    const [exportDateType, setExportDateType] = useState('dateAjout');
    const [exportCommercial, setExportCommercial] = useState('tous');
    const [exportEtatDossier, setExportEtatDossier] = useState('tous');
    const [exportTypeDossier, setExportTypeDossier] = useState('tous');
    const [exportStatutPaiement, setExportStatutPaiement] = useState('tous');
    const [allCommerciauxList, setAllCommerciauxList] = useState([]);

    const [selectedFields, setSelectedFields] = useState(() => {
        const initial = {};
        EXPORT_FIELD_GROUPS.forEach(g => {
            g.fields.forEach(f => {
                initial[f.key] = !!f.default;
            });
        });
        return initial;
    });

    const [openGroups, setOpenGroups] = useState({
        identification: true,
        dossier: true,
        dates: false,
        finance: true,
        echeances: true
    });

    const [previewCount, setPreviewCount] = useState(0);
    const [countLoading, setCountLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState('');
    
    const [currentPagePagination, setCurrentPagePagination] = useState(1);
    const itemsPerPage = 10;

    // Helper pour déterminer la vue initiale selon l'URL (ex: ?tab=paiements)
    const getInitialView = () => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab') || params.get('view');
        if (tab && ['overview', 'pipeline', 'steg', 'receptionne', 'paiements'].includes(tab)) {
            return tab;
        }
        return 'overview';
    };

    // Local state for active tab (view)
    const [view, setView] = useState(getInitialView);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab') || params.get('view');
        if (tab && ['overview', 'pipeline', 'steg', 'receptionne', 'paiements'].includes(tab)) {
            setView(tab);
        }
    }, [location.search]);

    useEffect(() => {
        if (!typeUser) return;
        if (typeUser !== "admin" && typeUser !== "moderateur") {
            onNavigate("profile");
            return;
        }
        if (typeUser === "moderateur" && !hasPermission("dashboard", "read")) {
            onNavigate("profile");
            return;
        }
    }, [onNavigate, typeUser, hasPermission]);

    useEffect(() => {
        fetchClientsStatus();
        fetchClientsPipeline();
    }, [period, selectedMonth, selectedYear, customDates.start, customDates.end]);

    const MOIS_LISTE = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    const getDateRange = () => {
        const now = new Date();
        const start = new Date(now);
        const end = new Date(now);

        if (period === 'month_year' || selectedMonth || selectedYear) {
            if (selectedMonth && selectedYear) {
                const mIndex = MOIS_LISTE.indexOf(selectedMonth);
                if (mIndex !== -1) {
                    const s = new Date(Number(selectedYear), mIndex, 1, 0, 0, 0, 0);
                    const e = new Date(Number(selectedYear), mIndex + 1, 0, 23, 59, 59, 999);
                    return { dateDebut: s.toISOString(), dateFin: e.toISOString(), mois: selectedMonth, annee: selectedYear };
                }
            } else if (selectedYear) {
                const s = new Date(Number(selectedYear), 0, 1, 0, 0, 0, 0);
                const e = new Date(Number(selectedYear), 11, 31, 23, 59, 59, 999);
                return { dateDebut: s.toISOString(), dateFin: e.toISOString(), annee: selectedYear };
            }
        }

        switch (period) {
            case 'today':
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'yesterday':
                start.setDate(start.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                end.setDate(end.getDate() - 1);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'this_week':
                const day = start.getDay() || 7; // Sunday is 0, make it 7
                if (day !== 1) start.setHours(-24 * (day - 1));
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'last_week':
                const lastWeekStart = new Date(now);
                const dayLW = lastWeekStart.getDay() || 7;
                lastWeekStart.setDate(lastWeekStart.getDate() - dayLW - 6);
                lastWeekStart.setHours(0, 0, 0, 0);
                const lastWeekEnd = new Date(lastWeekStart);
                lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
                lastWeekEnd.setHours(23, 59, 59, 999);
                return { dateDebut: lastWeekStart.toISOString(), dateFin: lastWeekEnd.toISOString() };
            case 'this_month':
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'last_month':
                start.setMonth(start.getMonth() - 1, 1);
                start.setHours(0, 0, 0, 0);
                end.setDate(0); // Last day of last month
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'this_year':
                start.setMonth(0, 1);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'custom':
                if (customDates.start && customDates.end) {
                    return { dateDebut: new Date(customDates.start).toISOString(), dateFin: new Date(customDates.end).toISOString() };
                }
                return {};
            default: // 'total'
                return {};
        }
    };

    const getExportDateRange = () => {
        const now = new Date();
        const start = new Date(now);
        const end = new Date(now);

        if (exportPeriod === 'month_year' || exportMonth || exportYear) {
            if (exportMonth && exportYear) {
                const mIndex = MOIS_LISTE.indexOf(exportMonth);
                if (mIndex !== -1) {
                    const s = new Date(Number(exportYear), mIndex, 1, 0, 0, 0, 0);
                    const e = new Date(Number(exportYear), mIndex + 1, 0, 23, 59, 59, 999);
                    return { dateDebut: s.toISOString(), dateFin: e.toISOString(), mois: exportMonth, annee: exportYear };
                }
            } else if (exportYear) {
                const s = new Date(Number(exportYear), 0, 1, 0, 0, 0, 0);
                const e = new Date(Number(exportYear), 11, 31, 23, 59, 59, 999);
                return { dateDebut: s.toISOString(), dateFin: e.toISOString(), annee: exportYear };
            }
        }

        switch (exportPeriod) {
            case 'today':
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'yesterday':
                start.setDate(start.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                end.setDate(end.getDate() - 1);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'this_week':
                const day = start.getDay() || 7;
                if (day !== 1) start.setHours(-24 * (day - 1));
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'last_week':
                const lastWeekStart = new Date(now);
                const dayLW = lastWeekStart.getDay() || 7;
                lastWeekStart.setDate(lastWeekStart.getDate() - dayLW - 6);
                lastWeekStart.setHours(0, 0, 0, 0);
                const lastWeekEnd = new Date(lastWeekStart);
                lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
                lastWeekEnd.setHours(23, 59, 59, 999);
                return { dateDebut: lastWeekStart.toISOString(), dateFin: lastWeekEnd.toISOString() };
            case 'this_month':
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'last_month':
                start.setMonth(start.getMonth() - 1, 1);
                start.setHours(0, 0, 0, 0);
                end.setDate(0);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'this_year':
                start.setMonth(0, 1);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return { dateDebut: start.toISOString(), dateFin: end.toISOString() };
            case 'custom':
                if (exportCustomDates.start && exportCustomDates.end) {
                    return { dateDebut: new Date(exportCustomDates.start).toISOString(), dateFin: new Date(exportCustomDates.end).toISOString() };
                }
                return {};
            default:
                return {};
        }
    };

    const fetchExportPreviewCount = async () => {
        try {
            setCountLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();

            const dates = getExportDateRange();
            if (dates.dateDebut && dates.dateFin) {
                params.append('dateDebut', dates.dateDebut);
                params.append('dateFin', dates.dateFin);
            }
            if (dates.mois) params.append('mois', dates.mois);
            if (dates.annee) params.append('annee', dates.annee);

            if (exportDateType) params.append('dateType', exportDateType);
            if (exportCommercial) params.append('commercial', exportCommercial);
            if (exportEtatDossier) params.append('etatDossier', exportEtatDossier);
            if (exportTypeDossier) params.append('typeDossier', exportTypeDossier);
            if (exportStatutPaiement) params.append('statutPaiement', exportStatutPaiement);

            const activeFieldKeys = Object.keys(selectedFields).filter(k => selectedFields[k]);
            params.append('fields', activeFieldKeys.join(','));

            const response = await axios.get(`${API_URL}/dashboard/clients-export-count?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setPreviewCount(response.data.count);
            }
        } catch (err) {
            console.error('Erreur fetchExportPreviewCount:', err);
        } finally {
            setCountLoading(false);
        }
    };

    useEffect(() => {
        if (!showExportModal) return;
        fetchExportPreviewCount();
    }, [
        showExportModal,
        exportPeriod,
        exportYear,
        exportMonth,
        exportCustomDates.start,
        exportCustomDates.end,
        exportDateType,
        exportCommercial,
        exportEtatDossier,
        exportTypeDossier,
        exportStatutPaiement,
        selectedFields
    ]);

    const handleOpenExportModal = async () => {
        setExportPeriod(period);
        setExportYear(selectedYear);
        setExportMonth(selectedMonth);
        setExportCustomDates(customDates);
        setExportCommercial(tableFilters.commercial || commercialFilter || 'tous');
        setShowExportModal(true);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/users/commerciaux`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && Array.isArray(res.data.data)) {
                setAllCommerciauxList(res.data.data.map(u => ({ id: u._id, nom: `${u.nom || ''} ${u.prenom || ''}`.trim() })));
            } else if (Array.isArray(res.data)) {
                setAllCommerciauxList(res.data.map(u => ({ id: u._id, nom: `${u.nom || ''} ${u.prenom || ''}`.trim() })));
            } else {
                setAllCommerciauxList(uniqueCommercials);
            }
        } catch (e) {
            setAllCommerciauxList(uniqueCommercials);
        }
    };

    const handleSelectAll = () => {
        const next = {};
        EXPORT_FIELD_GROUPS.forEach(g => g.fields.forEach(f => { next[f.key] = true; }));
        setSelectedFields(next);
    };

    const handleSelectDefault = () => {
        const next = {};
        EXPORT_FIELD_GROUPS.forEach(g => g.fields.forEach(f => { next[f.key] = !!f.default; }));
        setSelectedFields(next);
    };

    const handleDeselectAll = () => {
        const next = {};
        EXPORT_FIELD_GROUPS.forEach(g => g.fields.forEach(f => { next[f.key] = false; }));
        setSelectedFields(next);
    };

    const toggleField = (key) => {
        setSelectedFields(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleExecuteExport = async () => {
        const activeFieldKeys = Object.keys(selectedFields).filter(k => selectedFields[k]);
        if (activeFieldKeys.length === 0) {
            setExportError('Veuillez sélectionner au moins un champ à inclure dans l\'Excel.');
            return;
        }

        try {
            setIsExporting(true);
            setExportError('');

            const token = localStorage.getItem('token');
            const params = new URLSearchParams();

            const dates = getExportDateRange();
            if (dates.dateDebut && dates.dateFin) {
                params.append('dateDebut', dates.dateDebut);
                params.append('dateFin', dates.dateFin);
            }
            if (dates.mois) params.append('mois', dates.mois);
            if (dates.annee) params.append('annee', dates.annee);

            if (exportDateType) params.append('dateType', exportDateType);
            if (exportCommercial) params.append('commercial', exportCommercial);
            if (exportEtatDossier) params.append('etatDossier', exportEtatDossier);
            if (exportTypeDossier) params.append('typeDossier', exportTypeDossier);
            if (exportStatutPaiement) params.append('statutPaiement', exportStatutPaiement);

            params.append('fields', activeFieldKeys.join(','));

            const response = await axios.get(`${API_URL}/dashboard/clients-export-excel?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            let filename = 'SastraEnergy_Clients.xlsx';
            const disposition = response.headers['content-disposition'];
            if (disposition && disposition.includes('filename=')) {
                const matches = disposition.match(/filename="?([^";]+)"?/);
                if (matches && matches[1]) filename = matches[1];
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setShowExportModal(false);
        } catch (err) {
            console.error('Erreur téléchargement Excel:', err);
            setExportError('Une erreur est survenue lors de la génération du fichier Excel.');
        } finally {
            setIsExporting(false);
        }
    };

    const activeFieldsCount = useMemo(() => {
        return Object.values(selectedFields).filter(Boolean).length;
    }, [selectedFields]);

    const totalFieldsCount = useMemo(() => {
        return EXPORT_FIELD_GROUPS.reduce((acc, g) => acc + g.fields.length, 0);
    }, []);

    const fetchClientsStatus = async () => {
        // Prevent fetching if custom is selected but dates are missing
        if (period === 'custom' && (!customDates.start || !customDates.end)) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const dates = getDateRange();
            const params = new URLSearchParams();

            if (dates.dateDebut && dates.dateFin) {
                params.append('dateDebut', dates.dateDebut);
                params.append('dateFin', dates.dateFin);
            }
            if (dates.mois) params.append('mois', dates.mois);
            if (dates.annee) params.append('annee', dates.annee);
            
            let queryUrl = `${API_URL}/dashboard/clients-status`;
            const queryString = params.toString();
            if (queryString) {
                queryUrl += `?${queryString}`;
            }

            const response = await axios.get(queryUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setData(response.data.data);
            }
        } catch (error) {
            console.error('Erreur chargement clients status:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchClientsPipeline = async () => {
        if (period === 'custom' && (!customDates.start || !customDates.end)) return;
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const dates = getDateRange();
            const params = new URLSearchParams();

            if (dates.dateDebut && dates.dateFin) {
                params.append('dateDebut', dates.dateDebut);
                params.append('dateFin', dates.dateFin);
            }
            if (dates.mois) params.append('mois', dates.mois);
            if (dates.annee) params.append('annee', dates.annee);

            let queryUrl = `${API_URL}/dashboard/clients-pipeline`;
            const queryString = params.toString();
            if (queryString) {
                queryUrl += `?${queryString}`;
            }
            const response = await axios.get(queryUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setPipelineData(response.data.data);
            }
        } catch (error) {
            console.error('Erreur chargement pipeline:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter logic for lists
    const currentList = view === 'steg' ? data.stegNonPayes : 
                        (view === 'receptionne' ? data.receptionneNonCommences : 
                        (view === 'paiements' ? data.paiementsEffectues : []));
    
    const uniqueCommsTable = useMemo(() => {
        const comms = new Map();
        currentList.forEach(item => {
            if (item.commercialId) {
                comms.set(item.commercialId, item.commercial);
            }
        });
        return Array.from(comms.entries()).map(([id, nom]) => ({ id, nom })).sort((a, b) => a.nom.localeCompare(b.nom));
    }, [currentList]);

    const uniqueMethodsTable = useMemo(() => {
        const methods = new Set();
        currentList.forEach(item => {
            const method = view === 'paiements' ? item.modePaiementDetail : item.methodePaiement;
            if (method) methods.add(method.toUpperCase());
        });
        return Array.from(methods).sort();
    }, [currentList, view]);

    const uniqueStatsTable = useMemo(() => {
        const stats = new Set();
        currentList.forEach(item => {
            if (item.etatDossier) stats.add(item.etatDossier);
        });
        return Array.from(stats).sort();
    }, [currentList]);

    const filteredList = useMemo(() => {
        let list = [...currentList];

        // 1. Text Search
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            list = list.filter(item => 
                (item.clientNom && item.clientNom.toLowerCase().includes(lower)) ||
                (item.referenceDossier && item.referenceDossier.toLowerCase().includes(lower))
            );
        }

        // 2. Dropdown Filters
        if (tableFilters.commercial) {
            list = list.filter(item => item.commercialId === tableFilters.commercial);
        }
        if (tableFilters.methode) {
            list = list.filter(item => {
                const method = view === 'paiements' ? item.modePaiementDetail : item.methodePaiement;
                return method && method.toUpperCase() === tableFilters.methode;
            });
        }
        if (tableFilters.statut) {
            list = list.filter(item => item.etatDossier === tableFilters.statut);
        }

        // 3. Sorting
        list.sort((a, b) => {
            let valA, valB;
            switch(sortConfig.key) {
                case 'dateDesc':
                case 'dateAsc':
                    valA = new Date(view === 'paiements' ? a.datePaiementExacte : (view === 'steg' ? a.dateCreation : a.dateReception)).getTime();
                    valB = new Date(view === 'paiements' ? b.datePaiementExacte : (view === 'steg' ? b.dateCreation : b.dateReception)).getTime();
                    break;
                case 'nomAsc':
                case 'nomDesc':
                    valA = a.clientNom?.toLowerCase() || '';
                    valB = b.clientNom?.toLowerCase() || '';
                    break;
                case 'montantAsc':
                case 'montantDesc':
                    valA = view === 'paiements' ? (a.montant || 0) : (a.montantTotal || 0);
                    valB = view === 'paiements' ? (b.montant || 0) : (b.montantTotal || 0);
                    break;
                case 'statutAsc':
                case 'statutDesc':
                    valA = a.etatDossier?.toLowerCase() || '';
                    valB = b.etatDossier?.toLowerCase() || '';
                    break;
                default:
                    valA = 0; valB = 0;
            }

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return list;
    }, [searchTerm, currentList, tableFilters, sortConfig, view]);

    const totalPages = Math.ceil(filteredList.length / itemsPerPage);
    const paginatedList = filteredList.slice((currentPagePagination - 1) * itemsPerPage, currentPagePagination * itemsPerPage);

    // Reset pagination when search or view changes
    useEffect(() => {
        setCurrentPagePagination(1);
    }, [searchTerm, view, tableFilters, sortConfig]);

    const paiementsBreakdown = useMemo(() => {
        const map = new Map();
        let totalAmount = 0;
        data.paiementsEffectues.forEach(p => {
            const method = (p.modePaiementDetail || 'Inconnu').toUpperCase();
            if (!map.has(method)) {
                map.set(method, { count: 0, amount: 0 });
            }
            const stat = map.get(method);
            stat.count++;
            stat.amount += p.montant || 0;
            totalAmount += p.montant || 0;
        });

        return {
            totalAmount,
            methods: Array.from(map.entries()).map(([method, stat]) => ({
                method,
                count: stat.count,
                amount: stat.amount,
                percentage: totalAmount > 0 ? ((stat.amount / totalAmount) * 100).toFixed(1) : 0
            })).sort((a, b) => b.amount - a.amount)
        };
    }, [data.paiementsEffectues]);

    // --- RENDER VIEWS ---

    const renderTabs = () => (
        <div className="dcm-tabs-container">
            <button 
                className={`dcm-tab-btn ${view === 'overview' ? 'active' : ''}`}
                onClick={() => setView('overview')}
            >
                Vue d'ensemble
            </button>
            <button 
                className={`dcm-tab-btn ${view === 'pipeline' ? 'active' : ''}`}
                onClick={() => setView('pipeline')}
            >
                Vue Pipeline
            </button>
            <button 
                className={`dcm-tab-btn ${view === 'steg' ? 'active' : ''}`}
                onClick={() => setView('steg')}
            >
                STEG - Non Payé
                {data.stegNonPayes.length > 0 && <span className="dcm-tab-badge">{data.stegNonPayes.length}</span>}
            </button>
            <button 
                className={`dcm-tab-btn ${view === 'receptionne' ? 'active' : ''}`}
                onClick={() => setView('receptionne')}
            >
                Réceptionné - Non payé
                {data.receptionneNonCommences.length > 0 && <span className="dcm-tab-badge">{data.receptionneNonCommences.length}</span>}
            </button>
            <button 
                className={`dcm-tab-btn ${view === 'paiements' ? 'active' : ''}`}
                onClick={() => setView('paiements')}
            >
                Paiements Effectués
            </button>
        </div>
    );

    const handleViewDossier = (clientId, dossierId) => {
        onNavigate('detail-dossier', { clientId, dossierId });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(val || 0);
    };

    // --- RENDER VIEWS ---

    const renderDateFilter = () => (
        <div className="dcm-date-filter" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="dcm-filter-group">
                <Calendar size={18} className="dcm-filter-icon" />
                <select 
                    value={period} 
                    onChange={(e) => {
                        const val = e.target.value;
                        setPeriod(val);
                        if (val !== 'month_year') {
                            setSelectedMonth('');
                            setSelectedYear('');
                        }
                    }}
                    className="dcm-select"
                >
                    <option value="total">Depuis le début</option>
                    <option value="today">Aujourd'hui</option>
                    <option value="yesterday">Hier</option>
                    <option value="this_week">Cette semaine</option>
                    <option value="last_week">Semaine précédente</option>
                    <option value="this_month">Ce mois</option>
                    <option value="last_month">Mois précédent</option>
                    <option value="this_year">Cette année</option>
                    <option value="month_year">Sélection Mois & Année</option>
                    <option value="custom">Période personnalisée</option>
                </select>
            </div>

            {/* Sélecteur Année (en premier) */}
            <select
                className="dcm-select"
                value={selectedYear}
                onChange={(e) => {
                    const yVal = e.target.value;
                    setSelectedYear(yVal);
                    if (!yVal) {
                        setSelectedMonth('');
                    }
                    setPeriod('month_year');
                }}
            >
                <option value="">Toutes les années</option>
                {['2020', '2021', '2022', '2023', '2024', '2025', '2026', '2027', '2028', '2029', '2030'].map(y => (
                    <option key={y} value={y}>{y}</option>
                ))}
            </select>

            {/* Sélecteur Mois (en second, désactivé si aucune année sélectionnée) */}
            <select
                className="dcm-select"
                value={selectedMonth}
                disabled={!selectedYear}
                title={!selectedYear ? "Veuillez d'abord sélectionner une année" : "Sélectionnez un mois"}
                style={{
                    opacity: !selectedYear ? 0.55 : 1,
                    cursor: !selectedYear ? 'not-allowed' : 'pointer',
                    backgroundColor: !selectedYear ? '#f1f5f9' : '#ffffff'
                }}
                onChange={(e) => {
                    const mVal = e.target.value;
                    if (!selectedYear) return;
                    setSelectedMonth(mVal);
                    setPeriod('month_year');
                }}
            >
                <option value="">Tous les mois</option>
                {MOIS_LISTE.map(m => (
                    <option key={m} value={m}>{m}</option>
                ))}
            </select>

            {period === 'custom' && (
                <div className="dcm-custom-dates">
                    <input 
                        type="date" 
                        value={customDates.start} 
                        onChange={(e) => setCustomDates(p => ({ ...p, start: e.target.value }))}
                        className="dcm-date-input"
                    />
                    <span>au</span>
                    <input 
                        type="date" 
                        value={customDates.end} 
                        onChange={(e) => setCustomDates(p => ({ ...p, end: e.target.value }))}
                        className="dcm-date-input"
                    />
                </div>
            )}

            <button 
                className="dcm-export-trigger-btn" 
                onClick={handleOpenExportModal}
                title="Exporter les données au format Excel (.xlsx)"
            >
                <Download size={16} /> Exporter Excel
            </button>
        </div>
    );

    const renderOverview = () => (
        <div className="dcm-overview">
            <div className="dcm-overview-header">
                <h2 className="dcm-section-title">Vue d'ensemble Globale</h2>
            </div>
            <div className="dcm-stats-grid">
                <div className="dcm-stat-card total">
                    <div className="dcm-stat-icon"><Users size={24} /></div>
                    <div className="dcm-stat-content">
                        <h3>Total Dossiers</h3>
                        <p>{data.stats.totalDossiers}</p>
                    </div>
                </div>
                <div className="dcm-stat-card success">
                    <div className="dcm-stat-icon"><CheckCircle size={24} /></div>
                    <div className="dcm-stat-content">
                        <h3>Dossiers Payés</h3>
                        <p>{data.stats.dossiersPayes}</p>
                    </div>
                </div>
                <div className="dcm-stat-card warning">
                    <div className="dcm-stat-icon"><AlertTriangle size={24} /></div>
                    <div className="dcm-stat-content">
                        <h3>Dossiers Non Payés</h3>
                        <p>{data.stats.dossiersNonPayes}</p>
                    </div>
                </div>
                <div className="dcm-stat-card info">
                    <div className="dcm-stat-icon"><TrendingUp size={24} /></div>
                    <div className="dcm-stat-content">
                        <h3>Pourcentage de Paiement</h3>
                        <p>{data.stats.pourcentagePayes}%</p>
                    </div>
                </div>
            </div>

            <h2 className="dcm-section-title mt-4">Statistiques des Paiements (Période sélectionnée)</h2>
            <div className="dcm-stats-grid mb-4">
                <div className="dcm-stat-card info">
                    <div className="dcm-stat-icon"><CreditCard size={24} /></div>
                    <div className="dcm-stat-content">
                        <h3>Paiements Effectués</h3>
                        <p>{data.stats.totalPaiementsCount}</p>
                    </div>
                </div>
                <div className="dcm-stat-card success">
                    <div className="dcm-stat-icon"><DollarSign size={24} /></div>
                    <div className="dcm-stat-content">
                        <h3>Montant Encaissé Total</h3>
                        <p>{formatCurrency(data.stats.montantTotalEncaisse)}</p>
                    </div>
                </div>
            </div>

            <h3 className="dcm-section-subtitle">Répartition par Méthode de Paiement</h3>
            {paiementsBreakdown.methods.length > 0 ? (
                <div className="dcm-breakdown-grid">
                    {paiementsBreakdown.methods.map(pm => (
                        <div key={pm.method} className="dcm-breakdown-card">
                            <div className="dcm-breakdown-header">
                                <span className="dcm-breakdown-title">{pm.method}</span>
                                <span className="dcm-breakdown-percent">{pm.percentage}%</span>
                            </div>
                            <div className="dcm-breakdown-amount">{formatCurrency(pm.amount)}</div>
                            <div className="dcm-breakdown-count">{pm.count} paiement(s)</div>
                        </div>
                    ))}
                    <div className="dcm-breakdown-card total">
                        <div className="dcm-breakdown-header">
                            <span className="dcm-breakdown-title">TOTAL</span>
                            <span className="dcm-breakdown-percent">100%</span>
                        </div>
                        <div className="dcm-breakdown-amount">{formatCurrency(paiementsBreakdown.totalAmount)}</div>
                        <div className="dcm-breakdown-count">{data.stats.totalPaiementsCount} paiement(s)</div>
                    </div>
                </div>
            ) : (
                <div className="dcm-no-data-small">Aucun paiement encaissé sur cette période.</div>
            )}
        </div>
    );

    const renderListStats = () => {
        let totalMontant = 0;
        let totalRestant = 0;
        let totalPaye = 0;

        filteredList.forEach(item => {
            if (view === 'paiements') {
                totalPaye += (item.montant || 0);
            } else {
                totalMontant += (item.montantTotal || 0);
                const reste = (item.montantTotal || 0) - (item.montantPaye || 0);
                totalRestant += (reste > 0 ? reste : 0);
            }
        });

        return (
            <div className="dcm-list-stats">
                <div className="dcm-stat-mini">
                    <span className="dcm-stat-mini-label">Total Dossiers :</span>
                    <span className="dcm-stat-mini-value">{filteredList.length}</span>
                </div>
                {view !== 'paiements' && (
                    <>
                        <div className="dcm-stat-mini">
                            <span className="dcm-stat-mini-label">Montant Total :</span>
                            <span className="dcm-stat-mini-value fw-600">{formatCurrency(totalMontant)}</span>
                        </div>
                        <div className="dcm-stat-mini warning">
                            <span className="dcm-stat-mini-label">Montant Restant :</span>
                            <span className="dcm-stat-mini-value fw-600 color-danger">{formatCurrency(totalRestant)}</span>
                        </div>
                    </>
                )}
                {view === 'paiements' && (
                    <div className="dcm-stat-mini success">
                        <span className="dcm-stat-mini-label">Total Payé :</span>
                        <span className="dcm-stat-mini-value fw-600 color-success">{formatCurrency(totalPaye)}</span>
                    </div>
                )}
            </div>
        );
    };

    const uniqueCommercials = useMemo(() => {
        const comms = new Map();
        pipelineData.pipeline.forEach(step => {
            step.dossiers.forEach(d => {
                if (d.commercialId) {
                    comms.set(d.commercialId, d.commercial);
                }
            });
        });
        return Array.from(comms.entries()).map(([id, nom]) => ({ id, nom })).sort((a, b) => a.nom.localeCompare(b.nom));
    }, [pipelineData.pipeline]);

    const filteredPipeline = useMemo(() => {
        let activeTotal = 0;
        const newPipeline = pipelineData.pipeline.map(step => {
            const lowerSearch = searchTerm.toLowerCase();
            const filteredDossiers = step.dossiers.filter(d => {
                const matchesSearch = !searchTerm || 
                    (d.clientNom && d.clientNom.toLowerCase().includes(lowerSearch)) ||
                    (d.referenceDossier && d.referenceDossier.toLowerCase().includes(lowerSearch));
                const matchesCommercial = !commercialFilter || d.commercialId === commercialFilter;
                return matchesSearch && matchesCommercial;
            });
            activeTotal += filteredDossiers.length;
            return {
                ...step,
                count: filteredDossiers.length,
                dossiers: filteredDossiers
            };
        });

        // Recalculate percentages based on filtered total
        return {
            totalDossiers: activeTotal,
            pipeline: newPipeline.map(step => ({
                ...step,
                pourcentage: activeTotal > 0 ? ((step.count / activeTotal) * 100).toFixed(1) : 0
            }))
        };
    }, [pipelineData, searchTerm, commercialFilter]);

    const getStatusColorClass = (statut) => {
        const mapping = {
            "Déposé chez Sastra": "status-blue",
            "En attente d'approbation bureau d'étude": "status-orange",
            "Approuvé bureau d'étude": "status-cyan",
            "En attente d'approbation": "status-yellow",
            "En attente d'installation": "status-purple",
            "En attente de réception": "status-pink",
            "Réceptionné": "status-green",
            "Manque PV": "status-red",
            "Dossier en facturation": "status-indigo"
        };
        return mapping[statut] || "status-gray";
    };

    const renderPipeline = () => (
        <div className="dcm-pipeline-container">
            <div className="dcm-pipeline-header">
                <div className="dcm-pipeline-filters">
                    <div className="dcm-search-box">
                        <Search size={18} className="dcm-search-icon" />
                        <input 
                            type="text" 
                            className="dcm-search-input"
                            placeholder="Rechercher (Nom, Réf)..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="dcm-clear-search" onClick={() => setSearchTerm('')}>
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <select 
                        className="dcm-select"
                        value={commercialFilter}
                        onChange={(e) => setCommercialFilter(e.target.value)}
                    >
                        <option value="">Tous les commerciaux</option>
                        {uniqueCommercials.map(c => (
                            <option key={c.id} value={c.id}>{c.nom}</option>
                        ))}
                    </select>
                </div>
                <div className="dcm-pipeline-stats">
                    Total Dossiers Affichés : <strong>{filteredPipeline.totalDossiers}</strong>
                </div>
            </div>

            <div className="dcm-kanban-board">
                {filteredPipeline.pipeline.map((step, index) => (
                    <div key={index} className={`dcm-kanban-column ${getStatusColorClass(step.statut)}`}>
                        <div className="dcm-kanban-column-header">
                            <h3 title={step.statut}>{step.statut}</h3>
                            <div className="dcm-kanban-meta">
                                <span className="dcm-kanban-count">{step.count}</span>
                                <span className="dcm-kanban-percent">{step.pourcentage}%</span>
                            </div>
                        </div>
                        <div className="dcm-kanban-cards">
                            {step.dossiers.map(dossier => (
                                <div key={dossier.dossierId} className="dcm-kanban-card" onClick={() => handleViewDossier(dossier.clientId, dossier.dossierId)}>
                                    <div className="dcm-card-top">
                                        <span className="dcm-card-ref">{dossier.referenceDossier}</span>
                                        <span className="dcm-card-days" title={`Depuis le ${formatDate(dossier.dateEntreeStatut)}`}>
                                            {dossier.joursDansStatut} j
                                        </span>
                                    </div>
                                    <div className="dcm-card-client" title={dossier.clientNom}>{dossier.clientNom}</div>
                                    <div className="dcm-card-bottom">
                                        <span className="dcm-card-commercial">{dossier.commercial}</span>
                                        <span className="dcm-card-amount">{formatCurrency(dossier.montantTotal)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderTable = () => {
        let title = '';
        if (view === 'steg') title = 'STEG – Paiement non effectué';
        if (view === 'receptionne') title = 'Réceptionné – Non payé';
        if (view === 'paiements') title = 'Paiements effectués';

        return (
        <div className="dcm-table-container">
            <div className="dcm-table-header">
                <h2 className="dcm-section-title">{title}</h2>
            </div>
            
            <div className="dcm-table-filters-bar">
                <div className="dcm-search-box">
                    <Search size={18} className="dcm-search-icon" />
                    <input 
                        type="text" 
                        className="dcm-search-input"
                        placeholder="Chercher par nom, réf..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="dcm-clear-search" onClick={() => setSearchTerm('')}>
                            <X size={14} />
                        </button>
                    )}
                </div>

                <select 
                    className="dcm-select"
                    value={tableFilters.commercial}
                    onChange={(e) => setTableFilters({ ...tableFilters, commercial: e.target.value })}
                >
                    <option value="">Tous les commerciaux</option>
                    {uniqueCommsTable.map(c => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                </select>

                <select 
                    className="dcm-select"
                    value={tableFilters.methode}
                    onChange={(e) => setTableFilters({ ...tableFilters, methode: e.target.value })}
                >
                    <option value="">Toutes méthodes</option>
                    {uniqueMethodsTable.map(m => (
                        <option key={m} value={m}>{m}</option>
                    ))}
                </select>

                <select 
                    className="dcm-select"
                    value={tableFilters.statut}
                    onChange={(e) => setTableFilters({ ...tableFilters, statut: e.target.value })}
                >
                    <option value="">Tous les statuts</option>
                    {uniqueStatsTable.map(s => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>

                <select 
                    className="dcm-select sort-select"
                    value={`${sortConfig.key}-${sortConfig.direction}`}
                    onChange={(e) => {
                        const [key, direction] = e.target.value.split('-');
                        setSortConfig({ key, direction });
                    }}
                >
                    <option value="dateDesc-desc">Date (Plus récent)</option>
                    <option value="dateAsc-asc">Date (Plus ancien)</option>
                    <option value="nomAsc-asc">Nom (A - Z)</option>
                    <option value="nomDesc-desc">Nom (Z - A)</option>
                    <option value="montantDesc-desc">Montant (Décroissant)</option>
                    <option value="montantAsc-asc">Montant (Croissant)</option>
                    <option value="statutAsc-asc">Statut (A - Z)</option>
                </select>
            </div>

            {renderListStats()}

            <div className="dcm-table-wrapper">
                <table className="dcm-table">
                    <thead>
                        <tr>
                            <th>Client</th>
                            <th>Référence Dossier</th>
                            {view !== 'paiements' && view !== 'receptionne' && <th>Contact</th>}
                            {view === 'paiements' && <th>Montant Payé</th>}
                            {view !== 'paiements' && <th>Montant Total</th>}
                            <th>Méthode</th>
                            {view === 'paiements' && <th>Date Paiement</th>}
                            {view === 'steg' && <th>Date Création</th>}
                            {view === 'receptionne' && <th>Date Réception</th>}
                            {view !== 'paiements' && <th>Reste à Payer</th>}
                            <th>Statut</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedList.length === 0 ? (
                            <tr>
                                <td colSpan={view === 'paiements' ? "7" : (view === 'receptionne' ? "8" : "9")} className="dcm-no-data">Aucun résultat trouvé pour la période sélectionnée.</td>
                            </tr>
                        ) : (
                            paginatedList.map((item, idx) => {
                                const reste = (item.montantTotal || 0) - (item.montantPaye || 0);
                                return (
                                    <tr key={`${item.dossierId}-${idx}`}>
                                        <td className="fw-500">{item.clientNom}</td>
                                        <td><span className="dcm-badge-ref">{item.referenceDossier}</span></td>
                                        
                                        {view !== 'paiements' && view !== 'receptionne' && (
                                            <td>
                                                {item.clientTelephone && <div className="dcm-contact-text">{item.clientTelephone}</div>}
                                                {item.clientEmail && <div className="dcm-contact-text text-muted">{item.clientEmail}</div>}
                                            </td>
                                        )}
                                        
                                        {view === 'paiements' && (
                                            <td className="fw-600 color-success">{formatCurrency(item.montant)}</td>
                                        )}
                                        {view !== 'paiements' && (
                                            <td className="fw-600">{formatCurrency(item.montantTotal)}</td>
                                        )}

                                        <td>
                                            {view === 'paiements' ? (
                                                <span className="dcm-badge mixte">{item.modePaiementDetail?.toUpperCase()}</span>
                                            ) : (
                                                <span className={`dcm-badge ${item.methodePaiement}`}>
                                                    {item.methodePaiement.toUpperCase()}
                                                </span>
                                            )}
                                        </td>

                                        {view === 'paiements' && <td>{formatDate(item.datePaiementExacte)}</td>}
                                        {view === 'steg' && <td>{formatDate(item.dateCreation)}</td>}
                                        {view === 'receptionne' && <td>{formatDate(item.dateReception)}</td>}
                                        
                                        {view !== 'paiements' && (
                                            <td className="fw-600 color-danger">{formatCurrency(reste > 0 ? reste : 0)}</td>
                                        )}

                                        <td>
                                            {view === 'paiements' ? (
                                                <span className="dcm-badge-status success">Payé</span>
                                            ) : (
                                                <span className={`dcm-badge-status ${item.statutPaiement === 'Payé' ? 'success' : 'warning'}`}>
                                                    {item.statutPaiement}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <button 
                                                className="dcm-action-btn"
                                                onClick={() => handleViewDossier(item.clientId, item.dossierId)}
                                                title="Voir Dossier"
                                            >
                                                <FileText size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="dcm-pagination">
                    <button 
                        disabled={currentPagePagination === 1}
                        onClick={() => setCurrentPagePagination(prev => prev - 1)}
                    >
                        Précédent
                    </button>
                    <span>Page {currentPagePagination} sur {totalPages}</span>
                    <button 
                        disabled={currentPagePagination === totalPages}
                        onClick={() => setCurrentPagePagination(prev => prev + 1)}
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
    };

    if (loading && Object.keys(data.stats).length === 0) {
        return (
            <div className="backoffice-layout">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <main className="backoffice-content dcm-content">
                    <LoadingSpinner />
                </main>
            </div>
        );
    }

    const renderExportModal = () => {
        if (!showExportModal) return null;

        return (
            <div className="dcm-modal-overlay">
                <div className="dcm-modal-card" onClick={(e) => e.stopPropagation()}>
                    <div className="dcm-modal-header">
                        <div className="dcm-modal-header-title">
                            <div className="dcm-modal-icon"><Download size={22} /></div>
                            <div>
                                <h2>Exporter les clients</h2>
                                <p>Configurez les filtres et les colonnes pour générer votre fichier Excel.</p>
                            </div>
                        </div>
                        <button className="dcm-modal-close" onClick={() => !isExporting && setShowExportModal(false)}>
                            <X size={18} />
                        </button>
                    </div>

                    <div className="dcm-modal-body">
                        {/* 1. Section Filtres de Période et Métier */}
                        <div className="dcm-export-section">
                            <h3 className="dcm-export-section-title"><Filter size={16} /> 1. Filtres d'exportation</h3>
                            
                            <div className="dcm-export-grid">
                                <div className="dcm-form-group">
                                    <label>Période</label>
                                    <select 
                                        value={exportPeriod} 
                                        onChange={(e) => setExportPeriod(e.target.value)}
                                        className="dcm-select-full"
                                    >
                                        <option value="total">Depuis le début (Toutes les dates)</option>
                                        <option value="today">Aujourd'hui</option>
                                        <option value="yesterday">Hier</option>
                                        <option value="this_week">Cette semaine</option>
                                        <option value="last_week">Semaine précédente</option>
                                        <option value="this_month">Ce mois</option>
                                        <option value="last_month">Mois précédent</option>
                                        <option value="this_year">Cette année</option>
                                        <option value="month_year">Sélection Mois & Année</option>
                                        <option value="custom">Période personnalisée</option>
                                    </select>
                                </div>

                                {exportPeriod === 'month_year' && (
                                    <>
                                        <div className="dcm-form-group">
                                            <label>Année</label>
                                            <select 
                                                value={exportYear} 
                                                onChange={(e) => setExportYear(e.target.value)}
                                                className="dcm-select-full"
                                            >
                                                <option value="">Toutes les années</option>
                                                {['2020', '2021', '2022', '2023', '2024', '2025', '2026', '2027', '2028', '2029', '2030'].map(y => (
                                                    <option key={y} value={y}>{y}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="dcm-form-group">
                                            <label>Mois</label>
                                            <select 
                                                value={exportMonth} 
                                                onChange={(e) => setExportMonth(e.target.value)}
                                                className="dcm-select-full"
                                                disabled={!exportYear}
                                            >
                                                <option value="">Tous les mois</option>
                                                {MOIS_LISTE.map(m => (
                                                    <option key={m} value={m}>{m}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </>
                                )}

                                {exportPeriod === 'custom' && (
                                    <div className="dcm-form-group dcm-full-width">
                                        <label>Intervalle personnalisé</label>
                                        <div className="dcm-custom-date-flex">
                                            <input 
                                                type="date" 
                                                value={exportCustomDates.start} 
                                                onChange={(e) => setExportCustomDates(p => ({ ...p, start: e.target.value }))}
                                                className="dcm-date-input"
                                            />
                                            <span>à</span>
                                            <input 
                                                type="date" 
                                                value={exportCustomDates.end} 
                                                onChange={(e) => setExportCustomDates(p => ({ ...p, end: e.target.value }))}
                                                className="dcm-date-input"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="dcm-form-group">
                                    <label>Date prise en compte</label>
                                    <select 
                                        value={exportDateType} 
                                        onChange={(e) => setExportDateType(e.target.value)}
                                        className="dcm-select-full"
                                    >
                                        <option value="dateAjout">Date d'ajout / création</option>
                                        <option value="dateArrivDossier">Date d'arrivée dossier</option>
                                        <option value="dateDepotSteg">Date dépôt STEG</option>
                                        <option value="dateDemandeReception">Date demande réception</option>
                                        <option value="dateMiseEnService">Date mise en service</option>
                                    </select>
                                </div>

                                <div className="dcm-form-group">
                                    <label>Commercial</label>
                                    <select 
                                        value={exportCommercial} 
                                        onChange={(e) => setExportCommercial(e.target.value)}
                                        className="dcm-select-full"
                                    >
                                        <option value="tous">Tous les commerciaux</option>
                                        {allCommerciauxList.map(c => (
                                            <option key={c.id} value={c.id}>{c.nom}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="dcm-form-group">
                                    <label>État du dossier</label>
                                    <select 
                                        value={exportEtatDossier} 
                                        onChange={(e) => setExportEtatDossier(e.target.value)}
                                        className="dcm-select-full"
                                    >
                                        <option value="tous">Tous les états</option>
                                        <option value="Déposé chez Sastra">Déposé chez Sastra</option>
                                        <option value="En attente d'approbation bureau d'étude">En attente d'approbation bureau d'étude</option>
                                        <option value="Approuvé bureau d'étude">Approuvé bureau d'étude</option>
                                        <option value="En attente d'approbation">En attente d'approbation</option>
                                        <option value="En attente d'installation">En attente d'installation</option>
                                        <option value="En attente de réception">En attente de réception</option>
                                        <option value="Réceptionné">Réceptionné</option>
                                        <option value="Manque PV">Manque PV</option>
                                        <option value="Dossier en facturation">Dossier en facturation</option>
                                    </select>
                                </div>

                                <div className="dcm-form-group">
                                    <label>Type de dossier</label>
                                    <select 
                                        value={exportTypeDossier} 
                                        onChange={(e) => setExportTypeDossier(e.target.value)}
                                        className="dcm-select-full"
                                    >
                                        <option value="tous">Tous les types</option>
                                        <option value="steg">STEG Crédit</option>
                                        <option value="autofinancement">Autofinancement / Comptant</option>
                                        <option value="mixte">Mixte</option>
                                        <option value="pompage">Pompage</option>
                                    </select>
                                </div>

                                <div className="dcm-form-group">
                                    <label>Statut de paiement</label>
                                    <select 
                                        value={exportStatutPaiement} 
                                        onChange={(e) => setExportStatutPaiement(e.target.value)}
                                        className="dcm-select-full"
                                    >
                                        <option value="tous">Tous les statuts</option>
                                        <option value="paye">Payé</option>
                                        <option value="non_paye">Non payé</option>
                                        <option value="partiel">Partiellement payé</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* 2. Section Sélection des Champs */}
                        <div className="dcm-export-section mt-4">
                            <div className="dcm-export-section-header">
                                <h3 className="dcm-export-section-title"><Layers size={16} /> 2. Champs à inclure dans l'Excel</h3>
                                <div className="dcm-field-actions">
                                    <button className="dcm-link-btn" onClick={handleSelectAll}>Tout sélectionner</button>
                                    <span className="dcm-sep">•</span>
                                    <button className="dcm-link-btn" onClick={handleSelectDefault}>Réinitialiser la sélection</button>
                                    <span className="dcm-sep">•</span>
                                    <button className="dcm-link-btn" onClick={handleDeselectAll}>Tout désélectionner</button>
                                </div>
                            </div>

                            <div className="dcm-selected-count-badge">
                                Champs sélectionnés : <strong>{activeFieldsCount} / {totalFieldsCount}</strong>
                            </div>

                            <div className="dcm-field-groups">
                                {EXPORT_FIELD_GROUPS.map((group) => {
                                    const IconComp = group.icon;
                                    const isOpen = !!openGroups[group.id];
                                    const groupSelectedCount = group.fields.filter(f => !!selectedFields[f.key]).length;

                                    return (
                                        <div key={group.id} className={`dcm-group-accordion ${isOpen ? 'open' : ''}`}>
                                            <div 
                                                className="dcm-group-header" 
                                                onClick={() => setOpenGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                                            >
                                                <div className="dcm-group-title">
                                                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                    <IconComp size={16} className="dcm-group-icon" />
                                                    <span>{group.title}</span>
                                                    <span className={`dcm-group-badge ${groupSelectedCount > 0 ? 'active' : ''}`}>{groupSelectedCount} / {group.fields.length}</span>
                                                </div>
                                            </div>

                                            {isOpen && (
                                                <div className="dcm-group-fields">
                                                    {group.fields.map((field) => (
                                                        <label key={field.key} className={`dcm-checkbox-label ${field.isDetailSheet ? 'detail-sheet' : ''}`}>
                                                            <input 
                                                                type="checkbox" 
                                                                checked={!!selectedFields[field.key]} 
                                                                onChange={() => toggleField(field.key)}
                                                            />
                                                            <span>{field.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 3. Zone Récapitulative */}
                        <div className="dcm-recap-box mt-4">
                            <div className="dcm-recap-title">Récapitulatif de l'exportation</div>
                            <div className="dcm-recap-grid">
                                <div className="dcm-recap-item">
                                    <span>Dossiers trouvés :</span>
                                    <strong>{countLoading ? 'Calcul...' : previewCount}</strong>
                                </div>
                                <div className="dcm-recap-item">
                                    <span>Champs sélectionnés :</span>
                                    <strong>{activeFieldsCount}</strong>
                                </div>
                                <div className="dcm-recap-item">
                                    <span>Feuilles générées :</span>
                                    <strong>{selectedFields['detailEcheances'] ? 'Clients + Échéances (2 feuilles)' : 'Clients (1 feuille)'}</strong>
                                </div>
                            </div>
                        </div>

                        {exportError && (
                            <div className="dcm-export-error mt-3">
                                <AlertTriangle size={16} /> {exportError}
                            </div>
                        )}
                    </div>

                    <div className="dcm-modal-footer">
                        <button 
                            className="dcm-btn-secondary" 
                            onClick={() => { setExportPeriod('total'); handleSelectDefault(); }}
                            disabled={isExporting}
                        >
                            <RefreshCw size={14} /> Réinitialiser
                        </button>
                        <div className="dcm-modal-right-actions">
                            <button 
                                className="dcm-btn-cancel" 
                                onClick={() => setShowExportModal(false)}
                                disabled={isExporting}
                            >
                                Annuler
                            </button>
                            <button 
                                className="dcm-btn-primary" 
                                onClick={handleExecuteExport}
                                disabled={isExporting || activeFieldsCount === 0 || previewCount === 0}
                            >
                                {isExporting ? (
                                    <>
                                        <LoadingSpinner size="small" /> Génération...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} /> Exporter Excel (.xlsx)
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="backoffice-layout">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
            <main className="backoffice-content dcm-content">
                <div className="dcm-header-flex">
                    <div className="dcm-header-titles">
                        <h1>Dashboard Clients</h1>
                        <p>Suivi de la situation des clients concernant les paiements et dossiers.</p>
                    </div>
                    {renderDateFilter()}
                </div>

                {loading && <div className="dcm-loading-overlay">Actualisation des données...</div>}

                {renderTabs()}

                <div className="dcm-tab-content">
                    {view === 'overview' && renderOverview()}
                    {view === 'pipeline' && renderPipeline()}
                    {(view === 'steg' || view === 'receptionne' || view === 'paiements') && renderTable()}
                </div>
                
                {renderExportModal()}
            </main>
        </div>
    );
};

export default DashboardClientsMaster;
